import React, { useEffect, useState } from 'react';
import { UserRound, MapPin, Package, Loader2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import Tooltip from '../ui/Tooltip';
import { useStorageUrl } from '../chatHome/MemberAvatar';
import { getShotReferences, type ShotReferenceView, type DroppedReferenceView } from '../../services/studioService';

/**
 * THE PICTURES THIS SHOT WILL ACTUALLY BE MADE FROM.
 *
 * Owner 2026-09-12: *"we do visually generate reference images in our workflow, because that helps user
 * visually lock down the reference they provide to shot generation"*. The generating half existed; the LOCKING
 * DOWN did not, because the one place a creator commits a shot showed counts — *Who (2) · Objects (1)* — and
 * counts are exactly what a visual lock is not. A creator could approve a set plate, name the place on a shot,
 * and still have no way to know whether the render would use it, until the render came back.
 *
 * So this strip shows the same resolution the render runs (`studioShotReferences`), before a credit is spent.
 *
 * WHAT IS MISSING IS SHOWN TOO, and that is the more useful half. Three different absences look identical from
 * here — no picture made yet, a picture that lost its slot to a higher-priority reference, a photoreal face
 * that cannot be image-locked at all — and each has a different fix, so each says which it is. The photoreal
 * one is not even a fault: it is the provider's likeness filter, and identity is held by description and a
 * stable seed instead. Saying that plainly stops it reading as a broken feature.
 */

/** The role glyphs. One per kind, so the strip is readable without labels at thumbnail size. */
const GLYPH = { face: UserRound, place: MapPin, object: Package } as const;

/** What to say about a reference that will not travel. Plain, and each names its own fix. */
const REASON: Record<string, string> = {
  no_plate: 'No picture yet — make one on the series page',
  slots: 'No room left — faces come first',
  photoreal_face: 'Held by description and a steady seed, not a picture',
};

const Thumb: React.FC<{ item: ShotReferenceView }> = ({ item }) => {
  const url = useStorageUrl(item.path);
  const Glyph = GLYPH[item.kind];
  return (
    <Tooltip content={`${item.name} — the ${item.kind === 'face' ? 'person' : item.kind === 'place' ? 'place' : 'object'} this shot is built from`}>
      <span className="relative block h-12 w-12 shrink-0" data-testid="shot-reference">
        {url ? (
          <img
            src={url}
            alt={item.name}
            className="h-12 w-12 rounded-md border border-secondary-200 object-cover dark:border-secondary-700"
          />
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-md border border-secondary-200 dark:border-secondary-700">
            <Loader2 className="h-3 w-3 animate-spin text-secondary-400" aria-hidden />
          </span>
        )}
        {/* The glyph rides the corner rather than sitting beside the name: at this size the ROLE is what needs
            to read at a glance, and the name is a hover away. */}
        <span className="absolute -bottom-1 -right-1 rounded-full border border-secondary-200 bg-white p-0.5 dark:border-secondary-600 dark:bg-secondary-900">
          <Glyph className="h-2.5 w-2.5 text-secondary-500 dark:text-secondary-400" aria-hidden />
        </span>
      </span>
    </Tooltip>
  );
};

const Missing: React.FC<{ item: DroppedReferenceView }> = ({ item }) => {
  const Glyph = GLYPH[item.kind];
  return (
    <span
      data-testid="shot-reference-missing"
      className={`flex items-start gap-1.5 ${TYPE.micro} text-secondary-400 dark:text-secondary-500`}
    >
      <Glyph className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      <span className="min-w-0">
        <span className="text-secondary-500 dark:text-secondary-400">{item.name}</span>
        {' — '}
        {REASON[item.reason] || 'Not travelling with this shot'}
      </span>
    </span>
  );
};

export interface ShotReferencesProps {
  storyboardId?: string | null;
  index: number | null;
  /**
   * Changes whenever the shot's cast, objects or place change, so the strip re-asks. Passed in rather than
   * derived here: the inspector already holds the shot, and a deep compare on every render would be worse.
   */
  signature?: string;
  /**
   * False when the server has not been told about this shot yet (a starter shot, or one just added before
   * autosave lands). Nothing the server holds can travel with a shot it has never seen, so the strip answers
   * locally with the empty guidance instead of asking a question the route can only refuse (BUGS.md 2026-09-26).
   * The save that writes it bumps `signature`, and the strip asks then.
   */
  saved?: boolean;
}

const ShotReferences: React.FC<ShotReferencesProps> = ({ storyboardId, index, signature, saved = true }) => {
  const [state, setState] = useState<{ sent: ShotReferenceView[]; dropped: DroppedReferenceView[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!storyboardId || index == null) { setState(null); return; }
    if (!saved) { setState({ sent: [], dropped: [] }); setLoading(false); setFailed(false); return; }
    let alive = true;
    setLoading(true);
    setFailed(false);

    /**
     * ONE RETRY, and it is not defensive padding — it is the normal case for a shot that was just added.
     *
     * The episode autosaves on a debounce, and this asks the SERVER what a shot will use, so a shot selected
     * a heartbeat after it was created legitimately does not exist yet and the route says so. Waiting once and
     * asking again is what the creator would do, and by then the save has landed. (Found by the review walk,
     * which selects a shot immediately after adding it — the same race the plate flush fixed at the project
     * level, where an explicit save was the better answer because a button had been pressed.)
     */
    const ask = () => getShotReferences(storyboardId, index);
    ask()
      .catch(() => new Promise<ReturnType<typeof ask> extends Promise<infer T> ? T : never>((resolve, reject) => {
        setTimeout(() => { ask().then(resolve, reject); }, 1500);
      }))
      .then((r) => { if (alive) { setState({ sent: r.sent || [], dropped: r.dropped || [] }); setLoading(false); } })
      // QUIET on failure. This strip is a preview of something the render will do anyway; an error banner here
      // would be louder than the information is worth, and it must never look like the shot is broken.
      .catch(() => { if (alive) { setFailed(true); setState(null); setLoading(false); } });
    return () => { alive = false; };
  }, [storyboardId, index, signature, saved]);

  if (!storyboardId || index == null) return null;

  if (loading && !state) {
    return (
      <span className={`mt-1 flex items-center gap-1.5 ${TYPE.micro} text-secondary-400`} data-testid="shot-references-loading">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Checking what this shot will use…
      </span>
    );
  }
  if (failed) {
    return (
      <span className={`mt-1 block ${TYPE.micro} text-secondary-400`} data-testid="shot-references-error">
        Could not check what this shot will use.
      </span>
    );
  }
  if (!state) return null;
  if (!state.sent.length && !state.dropped.length) {
    return (
      <span className={`mt-1 block ${TYPE.micro} text-secondary-400`} data-testid="shot-references-empty">
        Nothing chosen yet. Name who is in it and where it happens, and their pictures travel with the shot.
      </span>
    );
  }

  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid="shot-references">
      {state.sent.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {state.sent.map((r) => <Thumb key={`${r.kind}-${r.path}`} item={r} />)}
        </div>
      )}
      {state.dropped.map((d, i) => <Missing key={`${d.kind}-${d.name}-${i}`} item={d} />)}
    </div>
  );
};

export default ShotReferences;
